import React, { useState, useEffect, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { FilterMatchMode } from "primereact/api";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { getItem } from "../../../../utils/localStorage";
import { makeRequest2 } from "../../../../utils/makeRequest";
import useAccountLookup from "../hooks/use_account_lookup";
import AccountNumberCell from "./AccountNumberCell";
import { fetchMissingUtilityCredits, mergeMissingCredits, creditSourceLabel } from "../hooks/missing_utility_credits";

const MpesaTransactionsTab = ({ showDashboard = true }) => {
  const { getAccount, openAccount } = useAccountLookup();
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    accountNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
    mpesaReceiptNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [activeTransactionTab, setActiveTransactionTab] = useState("all");

  // Advanced filtering state
  const [dateFilters, setDateFilters] = useState({
    startDate: null,
    endDate: null,
  });
  const [fieldFilters, setFieldFilters] = useState({
    accountNumber: "",
    mpesaReceiptNumber: "",
    amount: "",
    search: "",
  });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [stats, setStats] = useState({
    totalTransactions: 0,
    totalAmount: 0,
    todayTransactions: 0,
    todayAmount: 0,
    weeklyTransactions: 0,
    weeklyAmount: 0,
    monthlyTransactions: 0,
    monthlyAmount: 0,
    // Tab-specific stats
    allCount: 0,
    leaseCount: 0,
    levyCount: 0,
    powerCount: 0,
    waterCount: 0,
  });

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [lazyState, setLazyState] = useState({
    first: 0,
    rows: 10,
    page: 1,
    sortField: "createdAt",
    sortOrder: -1,
    filters: {
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    },
  });

  const dt = useRef(null);

  useEffect(() => {
    fetchTransactions();
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [
    activeTransactionTab,
    lazyState.page,
    lazyState.rows,
    dateFilters,
    fieldFilters,
  ]);

  useEffect(() => {
    if (transactions.length > 0) {
      calculateStatistics();
      filterTransactionsByTab(activeTransactionTab);
    }
  }, [transactions, activeTransactionTab]);

  const fetchTransactions = async (
    page = lazyState.page,
    limit = lazyState.rows,
  ) => {
    try {
      setLoading(true);
      const facilityId = await getItem("selectedFacilityId");

      // Build query parameters
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(lazyState.sortField && { sortBy: lazyState.sortField }),
        ...(lazyState.sortOrder && {
          sortOrder: lazyState.sortOrder === 1 ? "asc" : "desc",
        }),
      });

      // Add date filters
      if (dateFilters.startDate) {
        queryParams.append(
          "startDate",
          dateFilters.startDate.toISOString().split("T")[0],
        );
      }
      if (dateFilters.endDate) {
        queryParams.append(
          "endDate",
          dateFilters.endDate.toISOString().split("T")[0],
        );
      }

      // Add field filters
      if (fieldFilters.search && fieldFilters.search.trim()) {
        queryParams.append("search", fieldFilters.search.trim());
      }
      if (fieldFilters.accountNumber && fieldFilters.accountNumber.trim()) {
        queryParams.append("accountNumber", fieldFilters.accountNumber.trim());
      }
      if (
        fieldFilters.mpesaReceiptNumber &&
        fieldFilters.mpesaReceiptNumber.trim()
      ) {
        queryParams.append(
          "mpesaReceiptNumber",
          fieldFilters.mpesaReceiptNumber.trim(),
        );
      }
      if (fieldFilters.amount && fieldFilters.amount.trim()) {
        queryParams.append("amount", fieldFilters.amount.trim());
      }

      // Read through the backend, which forwards to the M-Pesa service
      const response = await makeRequest2(
        `/api/app/payment_management/mpesa/get_mpesa_transactions/${facilityId}?${queryParams}`,
        "GET",
      );

      if (!response?.success) {
        throw new Error(response?.error || "Failed to fetch transactions");
      }

      const data = response.data;

      // Power/water credits added without a matching M-Pesa transaction
      const credits = await fetchMissingUtilityCredits({
        facilityId,
        channel: "digital",
        startDate: dateFilters.startDate,
        endDate: dateFilters.endDate,
        search: fieldFilters.search || fieldFilters.accountNumber || fieldFilters.mpesaReceiptNumber,
      });
      setTransactions(
        mergeMissingCredits(data.transactions || [], credits, data.pagination),
      );

      // Update pagination state
      if (data.pagination) {
        setPagination(data.pagination);
      }
    } catch (err) {
      console.error("Error fetching transactions:", err);
    } finally {
      setLoading(false);
    }
  };

  const isPowerAccount = (accountNumber) => {
    if (!accountNumber) return false;
    // Check for prefix "2" (standard power)
    if (accountNumber.startsWith("2")) return true;
    // Special case for Northlands: prefix "1" with numeric value > 10 (e.g., 14532339547)
    if (accountNumber.startsWith("1")) {
      const numericValue = parseInt(accountNumber, 10);
      return !isNaN(numericValue) && numericValue > 10;
    }
    return false;
  };

  const isWaterAccount = (accountNumber) => {
    if (!accountNumber) return false;
    // Water accounts: prefix "1" but numeric value <= 10 (or not a valid large number)
    if (accountNumber.startsWith("1")) {
      const numericValue = parseInt(accountNumber, 10);
      return !isNaN(numericValue) && numericValue <= 10;
    }
    return false;
  };

  const filterTransactionsByTab = (tabType) => {
    let filtered = [];

    switch (tabType) {
      case "lease":
        filtered = transactions.filter(
          (transaction) =>
            transaction.accountNumber &&
            transaction.accountNumber.startsWith("6"),
        );
        break;
      case "levy":
        filtered = transactions.filter(
          (transaction) =>
            transaction.accountNumber &&
            transaction.accountNumber.startsWith("5"),
        );
        break;
      case "power":
        filtered = transactions.filter(
          (transaction) =>
            transaction.accountNumber && isPowerAccount(transaction.accountNumber),
        );
        break;
      case "water":
        filtered = transactions.filter(
          (transaction) =>
            transaction.accountNumber && isWaterAccount(transaction.accountNumber),
        );
        break;
      case "all":
      default:
        filtered = transactions;
        break;
    }

    setFilteredTransactions(filtered);
  };

  const calculateStatistics = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - today.getDay()); // Start of week (Sunday)
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1); // Start of month

    let totalAmount = 0;
    let todayCount = 0;
    let todayAmount = 0;
    let weeklyCount = 0;
    let weeklyAmount = 0;
    let monthlyCount = 0;
    let monthlyAmount = 0;

    // Count transactions by type
    let leaseCount = 0;
    let levyCount = 0;
    let powerCount = 0;
    let waterCount = 0;

    transactions.forEach((transaction) => {
      const transactionDate = new Date(transaction.createdAt);
      const amount = parseFloat(transaction.amount);
      const accountNumber = transaction.accountNumber || "";

      // Total
      totalAmount += amount;

      // Count by type based on account number prefix
      if (accountNumber.startsWith("6")) {
        leaseCount++;
      } else if (accountNumber.startsWith("5")) {
        levyCount++;
      } else if (isPowerAccount(accountNumber)) {
        powerCount++;
      } else if (isWaterAccount(accountNumber)) {
        waterCount++;
      }

      // Today's transactions
      if (transactionDate >= today) {
        todayCount++;
        todayAmount += amount;
      }

      // Weekly transactions
      if (transactionDate >= weekStart) {
        weeklyCount++;
        weeklyAmount += amount;
      }

      // Monthly transactions
      if (transactionDate >= monthStart) {
        monthlyCount++;
        monthlyAmount += amount;
      }
    });

    setStats({
      totalTransactions: pagination.totalCount || transactions.length,
      totalAmount,
      todayTransactions: todayCount,
      todayAmount,
      weeklyTransactions: weeklyCount,
      weeklyAmount,
      monthlyTransactions: monthlyCount,
      monthlyAmount,
      allCount: pagination.totalCount || transactions.length,
      leaseCount,
      levyCount,
      powerCount,
      waterCount,
    });
  };

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };

    _filters.global.value = value;

    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  // Filter handlers
  const handleDateFilterChange = (field, value) => {
    setDateFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Reset to first page when filters change
    setLazyState((prev) => ({ ...prev, page: 1, first: 0 }));
  };

  const handleFieldFilterChange = (field, value) => {
    setFieldFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Reset to first page when filters change
    setLazyState((prev) => ({ ...prev, page: 1, first: 0 }));
  };

  const clearAllFilters = () => {
    setDateFilters({ startDate: null, endDate: null });
    setFieldFilters({
      accountNumber: "",
      mpesaReceiptNumber: "",
      amount: "",
      search: "",
    });
    setGlobalFilterValue("");
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      accountNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
      mpesaReceiptNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
    setLazyState((prev) => ({ ...prev, page: 1, first: 0 }));
  };

  const applyQuickDateFilter = (days) => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    setDateFilters({ startDate, endDate });
    setLazyState((prev) => ({ ...prev, page: 1, first: 0 }));
  };

  const handleTabChange = (tabType) => {
    setActiveTransactionTab(tabType);
    // Reset to first page when changing tabs
    setLazyState((prev) => ({
      ...prev,
      page: 1,
      first: 0,
    }));
  };

  const onPage = (event) => {
    setLazyState((prev) => ({
      ...prev,
      first: event.first,
      rows: event.rows,
      page: event.page + 1,
    }));
  };

  const onSort = (event) => {
    setLazyState((prev) => ({
      ...prev,
      sortField: event.sortField,
      sortOrder: event.sortOrder,
    }));
  };

  const formatDateForExport = (dateStr) => {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${day} ${month} ${hours}:${minutes}:${seconds}`;
  };

  // Export functions
  const exportCSV = () => {
    // Sort transactions by date (latest first) and prepare clean data for CSV export
    const sortedTransactions = [...filteredTransactions].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
    );

    const csvData = sortedTransactions.map((transaction) => ({
      "Receipt Number": transaction.mpesaReceiptNumber || "",
      "Account Number": transaction.accountNumber || "",
      Amount: parseFloat(transaction.amount).toFixed(2),
      Date: formatDateForExport(transaction.createdAt),
      "Customer ID": transaction.customerId || "",
    }));

    // Convert to CSV manually to avoid BOM issues
    const headers = Object.keys(csvData[0]);
    const csvContent = [
      headers.join(","),
      ...csvData.map((row) =>
        headers
          .map((header) => {
            const value = row[header];
            // Escape commas and quotes in values
            return typeof value === "string" &&
              (value.includes(",") || value.includes('"'))
              ? `"${value.replace(/"/g, '""')}"`
              : value;
          })
          .join(","),
      ),
    ].join("\n");

    // Create and download the file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `mpesa-transactions-${activeTransactionTab}-${new Date().toISOString().split("T")[0]}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportPdf = () => {
    import("jspdf").then((jsPDF) => {
      import("jspdf-autotable").then(() => {
        const doc = new jsPDF.default();

        // Add title
        doc.setFontSize(18);
        doc.text(
          `M-Pesa Transactions Report - ${activeTransactionTab.charAt(0).toUpperCase() + activeTransactionTab.slice(1)}`,
          14,
          22,
        );

        // Add export date
        doc.setFontSize(11);
        doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 30);

        // Sort transactions by date (latest first) and prepare data for PDF
        const sortedTransactions = [...filteredTransactions].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
        );

        const exportData = sortedTransactions.map((transaction) => ({
          "Receipt Number": transaction.mpesaReceiptNumber || "",
          "Account Number": transaction.accountNumber || "",
          Amount: `KES ${parseFloat(transaction.amount).toFixed(2)}`,
          Date: formatDateForExport(transaction.createdAt),
          "Customer ID": transaction.customerId || "",
        }));

        // Create the table
        doc.autoTable({
          head: [Object.keys(exportData[0])],
          body: exportData.map((row) => Object.values(row)),
          startY: 40,
          styles: {
            fontSize: 8,
            cellPadding: 3,
          },
          headStyles: {
            fillColor: [41, 128, 185],
            textColor: 255,
            fontStyle: "bold",
          },
          alternateRowStyles: {
            fillColor: [245, 245, 245],
          },
        });

        // Save the PDF
        doc.save(
          `mpesa-transactions-${activeTransactionTab}-${new Date().toISOString().split("T")[0]}.pdf`,
        );
      });
    });
  };

  const exportExcel = () => {
    import("xlsx")
      .then((xlsx) => {
        // Sort transactions by date (latest first) and prepare data with proper formatting
        const sortedTransactions = [...filteredTransactions].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
        );

        const exportData = sortedTransactions.map((transaction) => ({
          "Receipt Number": transaction.mpesaReceiptNumber || "",
          "Account Number": transaction.accountNumber || "",
          Amount: parseFloat(transaction.amount),
          Date: formatDateForExport(transaction.createdAt),
          "Customer ID": transaction.customerId || "",
        }));

        const worksheet = xlsx.utils.json_to_sheet(exportData);

        // Auto-size columns
        const colWidths = [
          { wch: 15 }, // Receipt Number
          { wch: 15 }, // Account Number
          { wch: 12 }, // Amount
          { wch: 20 }, // Date
          { wch: 25 }, // Customer ID
        ];
        worksheet["!cols"] = colWidths;

        const workbook = {
          Sheets: { "M-Pesa Transactions": worksheet },
          SheetNames: ["M-Pesa Transactions"],
        };

        const excelBuffer = xlsx.write(workbook, {
          bookType: "xlsx",
          type: "array",
        });

        saveAsExcelFile(
          excelBuffer,
          `mpesa-transactions-${activeTransactionTab}`,
        );
      })
      .catch((error) => {
        console.error("Error loading xlsx library:", error);
        alert("Failed to export Excel file. Please try again.");
      });
  };

  const saveAsExcelFile = (buffer, fileName) => {
    try {
      const EXCEL_TYPE =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
      const EXCEL_EXTENSION = ".xlsx";
      const data = new Blob([buffer], { type: EXCEL_TYPE });

      // Create download link
      const link = document.createElement("a");
      const url = URL.createObjectURL(data);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        fileName + "_export_" + new Date().getTime() + EXCEL_EXTENSION,
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error saving Excel file:", error);
      alert("Failed to save Excel file. Please try again.");
    }
  };

  const renderHeader = () => {
    return (
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              value={fieldFilters.search}
              onChange={(e) =>
                handleFieldFilterChange("search", e.target.value)
              }
              placeholder="Quick search..."
              className="form-control"
            />
          </span>
          <Button
            type="button"
            icon={showAdvancedFilters ? "pi pi-angle-up" : "pi pi-angle-down"}
            label="Filters"
            className="p-button-outlined p-button-sm"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          />
          <Button
            type="button"
            icon="pi pi-times"
            label="Clear"
            className="p-button-outlined p-button-sm"
            onClick={clearAllFilters}
            disabled={
              !dateFilters.startDate &&
              !dateFilters.endDate &&
              !fieldFilters.search &&
              !fieldFilters.accountNumber &&
              !fieldFilters.mpesaReceiptNumber &&
              !fieldFilters.amount
            }
          />
        </div>
        <div className="d-flex align-items-center">
          {/* Export buttons */}
          <div className="btn-group me-2">
            <button
              type="button"
              className="btn btn-success btn-sm"
              onClick={exportCSV}
              disabled={loading || filteredTransactions.length === 0}
              title="Export as CSV"
            >
              <i className="fa fa-file-csv me-1"></i>
              CSV
            </button>
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={exportPdf}
              disabled={loading || filteredTransactions.length === 0}
              title="Export as PDF"
            >
              <i className="fa fa-file-pdf me-1"></i>
              PDF
            </button>
            <button
              type="button"
              className="btn btn-info btn-sm"
              onClick={exportExcel}
              disabled={loading || filteredTransactions.length === 0}
              title="Export as Excel"
            >
              <i className="fa fa-file-excel me-1"></i>
              Excel
            </button>
          </div>

          <button
            className="btn btn-primary btn-sm"
            onClick={() => fetchTransactions()}
            disabled={loading}
          >
            <i className="fa fa-refresh me-1"></i>
            Refresh
          </button>
        </div>
      </div>
    );
  };

  const renderAdvancedFilters = () => {
    if (!showAdvancedFilters) return null;

    return (
      <Card className="mb-3">
        <div className="row g-3">
          {/* Date Range Filters */}
          <div className="col-md-6">
            <div className="card">
              <div className="card-body">
                <h6 className="card-title">📅 Date Range</h6>
                <div className="row g-2">
                  <div className="col-6">
                    <label className="form-label small">From Date</label>
                    <Calendar
                      value={dateFilters.startDate}
                      onChange={(e) =>
                        handleDateFilterChange("startDate", e.value)
                      }
                      placeholder="Start date"
                      dateFormat="yy-mm-dd"
                      showIcon
                      className="w-100"
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label small">To Date</label>
                    <Calendar
                      value={dateFilters.endDate}
                      onChange={(e) =>
                        handleDateFilterChange("endDate", e.value)
                      }
                      placeholder="End date"
                      dateFormat="yy-mm-dd"
                      showIcon
                      className="w-100"
                    />
                  </div>
                </div>
                <div className="mt-2">
                  <small className="text-muted">Quick filters: </small>
                  <button
                    className="btn btn-outline-primary btn-sm me-1"
                    onClick={() => applyQuickDateFilter(1)}
                  >
                    Today
                  </button>
                  <button
                    className="btn btn-outline-primary btn-sm me-1"
                    onClick={() => applyQuickDateFilter(7)}
                  >
                    7 Days
                  </button>
                  <button
                    className="btn btn-outline-primary btn-sm me-1"
                    onClick={() => applyQuickDateFilter(30)}
                  >
                    30 Days
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Field-Specific Filters */}
          <div className="col-md-6">
            <div className="card">
              <div className="card-body">
                <h6 className="card-title">🔍 Specific Filters</h6>
                <div className="row g-2">
                  <div className="col-6">
                    <label className="form-label small">Account Number</label>
                    <InputText
                      value={fieldFilters.accountNumber}
                      onChange={(e) =>
                        handleFieldFilterChange("accountNumber", e.target.value)
                      }
                      placeholder="Filter by account"
                      className="w-100"
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label small">Receipt Number</label>
                    <InputText
                      value={fieldFilters.mpesaReceiptNumber}
                      onChange={(e) =>
                        handleFieldFilterChange(
                          "mpesaReceiptNumber",
                          e.target.value,
                        )
                      }
                      placeholder="Filter by receipt"
                      className="w-100"
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label small">Amount</label>
                    <InputText
                      value={fieldFilters.amount}
                      onChange={(e) =>
                        handleFieldFilterChange("amount", e.target.value)
                      }
                      placeholder="Exact amount"
                      className="w-100"
                      keyfilter="pnum"
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label small">Results per page</label>
                    <Dropdown
                      value={lazyState.rows}
                      options={[
                        { label: "10", value: 10 },
                        { label: "25", value: 25 },
                        { label: "50", value: 50 },
                        { label: "100", value: 100 },
                      ]}
                      onChange={(e) =>
                        setLazyState((prev) => ({
                          ...prev,
                          rows: e.value,
                          first: 0,
                          page: 1,
                        }))
                      }
                      className="w-100"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>
    );
  };

  const formatDate = (value) => {
    return new Date(value).toLocaleString();
  };

  const formatCurrency = (value) => {
    return `KES ${parseFloat(value).toFixed(2)}`;
  };

  const receiptTemplate = (rowData) => {
    const source = creditSourceLabel(rowData);
    return (
      <span>
        {rowData.mpesaReceiptNumber}
        {source && (
          <span className="badge bg-info ms-2" title="Added as a meter credit with no matching M-Pesa transaction">
            {source}
          </span>
        )}
      </span>
    );
  };

  const accountTemplate = (rowData) => (
    <AccountNumberCell
      accountNumber={rowData.accountNumber}
      getAccount={getAccount}
      openAccount={openAccount}
    />
  );

  const dateTemplate = (rowData) => {
    const date = new Date(rowData.createdAt);
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${day} ${month} ${hours}:${minutes}:${seconds}`;
  };

  const amountTemplate = (rowData) => {
    return formatCurrency(rowData.amount);
  };

  const header = renderHeader();

  const renderPaginationInfo = () => {
    const start = lazyState.first + 1;
    const end = Math.min(
      lazyState.first + lazyState.rows,
      pagination.totalCount,
    );

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div>
          <small className="text-muted">
            Showing {start} to {end} of {pagination.totalCount} entries
            {(dateFilters.startDate ||
              dateFilters.endDate ||
              Object.values(fieldFilters).some((f) => f)) && (
              <span className="ms-2">
                <i className="fa fa-filter text-primary"></i> Filtered
              </span>
            )}
          </small>
        </div>
        <div>
          <small className="text-muted">
            Page {pagination.currentPage} of {pagination.totalPages}
          </small>
        </div>
      </div>
    );
  };

  const renderStatisticsDashboard = () => {
    if (!showDashboard) return null;

    return (
      <div className="statistics-dashboard mb-4">
        <div className="row">
          {/* Total Transactions Card */}
          <div className="col-md-3 col-sm-6 mb-3">
            <div className="card h-100 border-left-primary shadow">
              <div className="card-body">
                <div className="row no-gutters align-items-center">
                  <div className="col mr-2">
                    <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">
                      Total Transactions
                    </div>
                    <div className="h5 mb-0 font-weight-bold text-gray-800">
                      {filteredTransactions.length}
                    </div>
                  </div>
                  <div className="col-auto">
                    <i className="fa fa-calendar fa-2x text-gray-300"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Total Amount Card */}
          <div className="col-md-3 col-sm-6 mb-3">
            <div className="card h-100 border-left-success shadow">
              <div className="card-body">
                <div className="row no-gutters align-items-center">
                  <div className="col mr-2">
                    <div className="text-xs font-weight-bold text-success text-uppercase mb-1">
                      Total Amount
                    </div>
                    <div className="h5 mb-0 font-weight-bold text-gray-800">
                      {formatCurrency(
                        filteredTransactions.reduce(
                          (sum, transaction) =>
                            sum + parseFloat(transaction.amount),
                          0,
                        ),
                      )}
                    </div>
                  </div>
                  <div className="col-auto">
                    <i className="fa fa-money-bill fa-2x text-gray-300"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Today's Transactions Card */}
          <div className="col-md-3 col-sm-6 mb-3">
            <div className="card h-100 border-left-info shadow">
              <div className="card-body">
                <div className="row no-gutters align-items-center">
                  <div className="col mr-2">
                    <div className="text-xs font-weight-bold text-info text-uppercase mb-1">
                      Today's Transactions
                    </div>
                    <div className="h5 mb-0 font-weight-bold text-gray-800">
                      {stats.todayTransactions} (
                      {formatCurrency(stats.todayAmount)})
                    </div>
                  </div>
                  <div className="col-auto">
                    <i className="fa fa-money-bill-wave fa-2x text-gray-300"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Monthly Transactions Card */}
          <div className="col-md-3 col-sm-6 mb-3">
            <div className="card h-100 border-left-warning shadow">
              <div className="card-body">
                <div className="row no-gutters align-items-center">
                  <div className="col mr-2">
                    <div className="text-xs font-weight-bold text-warning text-uppercase mb-1">
                      Monthly Transactions
                    </div>
                    <div className="h5 mb-0 font-weight-bold text-gray-800">
                      {stats.monthlyTransactions} (
                      {formatCurrency(stats.monthlyAmount)})
                    </div>
                  </div>
                  <div className="col-auto">
                    <i className="fa fa-chart-line fa-2x text-gray-300"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Second row of stats */}
        <div className="row">
          {/* Weekly Transactions */}
          <div className="col-md-6 mb-3">
            <div className="card h-100 border-left-danger shadow">
              <div className="card-body">
                <div className="row no-gutters align-items-center">
                  <div className="col mr-2">
                    <div className="text-xs font-weight-bold text-danger text-uppercase mb-1">
                      Weekly Transactions
                    </div>
                    <div className="h5 mb-0 font-weight-bold text-gray-800">
                      {stats.weeklyTransactions} (
                      {formatCurrency(stats.weeklyAmount)})
                    </div>
                  </div>
                  <div className="col-auto">
                    <i className="fa fa-calendar-week fa-2x text-gray-300"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Average Transaction Amount */}
          <div className="col-md-6 mb-3">
            <div className="card h-100 border-left-secondary shadow">
              <div className="card-body">
                <div className="row no-gutters align-items-center">
                  <div className="col mr-2">
                    <div className="text-xs font-weight-bold text-secondary text-uppercase mb-1">
                      Average Transaction Value
                    </div>
                    <div className="h5 mb-0 font-weight-bold text-gray-800">
                      {formatCurrency(
                        filteredTransactions.length > 0
                          ? filteredTransactions.reduce(
                              (sum, transaction) =>
                                sum + parseFloat(transaction.amount),
                              0,
                            ) / filteredTransactions.length
                          : 0,
                      )}
                    </div>
                  </div>
                  <div className="col-auto">
                    <i className="fa fa-calculator fa-2x text-gray-300"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTransactionTypeTabs = () => {
    return (
      <div className="mb-4">
        <ul className="nav nav-tabs" role="tablist">
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTransactionTab === "all" ? "active" : ""}`}
              type="button"
              role="tab"
              onClick={() => handleTabChange("all")}
            >
              <span className="d-flex align-items-center gap-2">
                <i className="fa fa-list me-1"></i>
                All
                <span className="badge bg-secondary rounded-pill">
                  {stats.allCount}
                </span>
              </span>
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTransactionTab === "lease" ? "active" : ""}`}
              type="button"
              role="tab"
              onClick={() => handleTabChange("lease")}
            >
              <span className="d-flex align-items-center gap-2">
                <i className="fa fa-home me-1"></i>
                Lease
                <span className="badge bg-primary rounded-pill">
                  {stats.leaseCount}
                </span>
              </span>
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTransactionTab === "levy" ? "active" : ""}`}
              type="button"
              role="tab"
              onClick={() => handleTabChange("levy")}
            >
              <span className="d-flex align-items-center gap-2">
                <i className="fa fa-receipt me-1"></i>
                Levy
                <span className="badge bg-info rounded-pill">
                  {stats.levyCount}
                </span>
              </span>
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTransactionTab === "power" ? "active" : ""}`}
              type="button"
              role="tab"
              onClick={() => handleTabChange("power")}
            >
              <span className="d-flex align-items-center gap-2">
                <i className="fa fa-bolt me-1"></i>
                Power
                <span className="badge bg-warning rounded-pill">
                  {stats.powerCount}
                </span>
              </span>
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTransactionTab === "water" ? "active" : ""}`}
              type="button"
              role="tab"
              onClick={() => handleTabChange("water")}
            >
              <span className="d-flex align-items-center gap-2">
                <i className="fa fa-tint me-1"></i>
                Water
                <span className="badge bg-success rounded-pill">
                  {stats.waterCount}
                </span>
              </span>
            </button>
          </li>
        </ul>
      </div>
    );
  };

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="card-title mb-0">M-Pesa Transactions</h5>
      </div>
      <div className="card-body">
        {/* Statistics Dashboard */}
        {renderStatisticsDashboard()}

        {/* Transaction Type Tabs */}
        {renderTransactionTypeTabs()}

        {/* Advanced Filters */}
        {renderAdvancedFilters()}

        {filteredTransactions.length === 0 && !loading ? (
          <div className="alert alert-info">
            <i className="fa fa-info-circle me-2"></i>
            No M-Pesa transactions found for this category.
          </div>
        ) : (
          <DataTable
            ref={dt}
            value={filteredTransactions}
            lazy
            paginator
            first={lazyState.first}
            rows={lazyState.rows}
            totalRecords={pagination.totalCount}
            onPage={onPage}
            onSort={onSort}
            sortField={lazyState.sortField}
            sortOrder={lazyState.sortOrder}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            dataKey="_id"
            filters={filters}
            loading={loading}
            globalFilterFields={["mpesaReceiptNumber", "accountNumber"]}
            header={header}
            emptyMessage="No transactions found"
            className="p-datatable-sm"
          >
            <Column
              field="mpesaReceiptNumber"
              header="Receipt Number"
              body={receiptTemplate}
              sortable
              filter
              filterPlaceholder="Search receipt"
            />
            <Column
              field="accountNumber"
              header="Account Number"
              body={accountTemplate}
              sortable
              filter
              filterPlaceholder="Search account"
            />
            <Column
              field="amount"
              header="Amount"
              body={amountTemplate}
              sortable
            />
            <Column
              field="createdAt"
              header="Date"
              body={dateTemplate}
              sortable
            />
            <Column field="customerId" header="Customer ID" sortable />
          </DataTable>
        )}

        {/* Pagination Info */}
        {!loading && pagination.totalCount > 0 && renderPaginationInfo()}
      </div>
    </div>
  );
};

export default MpesaTransactionsTab;
